import type { CaptchaChallenge } from '@deizi/shared';
import { useEffect, useRef, useState } from 'react';
import { useTheme } from '../lib/theme';
import { Icon } from './Icon';

/** The bits of the providers' browser APIs we use (explicit render). */
interface WidgetOptions {
  sitekey: string;
  theme: 'light' | 'dark';
  callback: (token: string) => void;
  'expired-callback': () => void;
  'error-callback'?: () => void;
  language?: string;
}
interface Turnstile {
  render: (el: HTMLElement, opts: WidgetOptions) => string;
  remove: (id: string) => void;
}
interface ReCaptcha {
  ready: (fn: () => void) => void;
  render: (el: HTMLElement, opts: WidgetOptions) => number;
}
declare global {
  interface Window {
    turnstile?: Turnstile;
    grecaptcha?: ReCaptcha;
  }
}

const SCRIPT = {
  turnstile: 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit',
  recaptcha: 'https://www.google.com/recaptcha/api.js?render=explicit&hl=pt-BR',
} as const;

const loading = new Map<string, Promise<void>>();

/** Loads the provider's script once; resolves when its global is ready. */
function loadProvider(provider: CaptchaChallenge['provider']): Promise<void> {
  const ready = () => (provider === 'turnstile' ? window.turnstile : window.grecaptcha);
  if (ready()) return Promise.resolve();
  let promise = loading.get(provider);
  if (!promise) {
    promise = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SCRIPT[provider];
      script.async = true;
      script.addEventListener('load', () => resolve());
      script.addEventListener('error', () => {
        loading.delete(provider);
        reject(new Error('captcha script failed to load'));
      });
      document.head.appendChild(script);
    });
    loading.set(provider, promise);
  }
  return promise;
}

/**
 * The captcha the API asks for after repeated sign-in failures (reCAPTCHA v2 checkbox or
 * Cloudflare Turnstile). Calls `onToken` with the response token, or null when it expires.
 * Remount it (change its `key`) to get a fresh challenge after a failed attempt.
 */
export function Captcha({
  challenge,
  onToken,
}: {
  challenge: CaptchaChallenge;
  onToken: (token: string | null) => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const { dark } = useTheme();
  const [failed, setFailed] = useState(false);
  const tokenRef = useRef(onToken);
  useEffect(() => {
    tokenRef.current = onToken;
  }, [onToken]);

  useEffect(() => {
    let cancelled = false;
    let turnstileId: string | undefined;
    const el = box.current;
    const opts: WidgetOptions = {
      sitekey: challenge.siteKey,
      theme: dark ? 'dark' : 'light',
      callback: (token) => tokenRef.current(token),
      'expired-callback': () => tokenRef.current(null),
      'error-callback': () => setFailed(true),
    };
    loadProvider(challenge.provider)
      .then(() => {
        if (cancelled || !el) return;
        if (challenge.provider === 'turnstile' && window.turnstile) {
          turnstileId = window.turnstile.render(el, { ...opts, language: 'pt-br' });
        } else if (window.grecaptcha) {
          const g = window.grecaptcha;
          g.ready(() => {
            if (!cancelled) g.render(el, opts);
          });
        }
      })
      .catch(() => setFailed(true));
    return () => {
      cancelled = true;
      if (turnstileId) window.turnstile?.remove(turnstileId);
      // reCAPTCHA has no remove: the container goes away with the component.
      if (el) el.replaceChildren();
    };
  }, [challenge.provider, challenge.siteKey, dark]);

  return (
    <div className="col" style={{ gap: 6, alignItems: 'center' }}>
      <div ref={box} data-testid="captcha" style={{ minHeight: 65 }} />
      {failed ? (
        <span className="help err" role="alert">
          <Icon name="alert" size={14} />
          Não foi possível carregar a verificação. Confira a conexão e recarregue a página.
        </span>
      ) : null}
    </div>
  );
}
